import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Bookmark, Check, ChevronDown, Pencil, Save, Settings2, Share2, Trash2, Users } from 'lucide-react';
import { MAX_TEMPLATES, type HomeTemplate } from '@tradeoutlet/shared';
import { api, errorMessage } from '@/lib/api';
import { queryClient } from '@/lib/queryClient';
import { useUi } from '@/lib/store';
import { DialogFooter, Button, Field, Input, Menu, MenuItem, Modal, Select, ask } from '@/components/ui';

/** Backlog #29: switch between saved home layouts, save the current one, and manage (rename, delete, share). */
export function TemplatesMenu({ ws }: { ws: string }) {
  const toast = useUi(s => s.toast);
  const q = useQuery({ queryKey: ['home', ws, 'templates'], queryFn: () => api.home.templates(ws) });
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [managing, setManaging] = useState(false);
  const list = q.data?.templates ?? [];
  const active = list.find(t => t.active);
  const mine = list.filter(t => t.mine);
  const shared = list.filter(t => !t.mine);

  const apply = async (t: HomeTemplate) => {
    try {
      queryClient.setQueryData(['home', ws, 'layout'], await api.home.applyTemplate(ws, t.id));
      void queryClient.invalidateQueries({ queryKey: ['home', ws, 'templates'] });
      toast(`Switched to “${t.name}”`, 'success');
    } catch (e) { toast(errorMessage(e), 'error'); }
  };

  return (
    <>
      <Menu open={open} setOpen={setOpen} trigger={
        <Button variant="ghost" data-testid="layouts-menu"><Bookmark className="h-4 w-4" />{active ? active.name : 'Layouts'}<ChevronDown className="h-3.5 w-3.5" /></Button>
      }>
        <div className="min-w-64">
          {mine.length > 0 && <p className="px-3 pb-1 pt-1.5 text-xs font-semibold uppercase text-slate-500">My layouts</p>}
          {mine.map(t => <TemplateRow key={t.id} t={t} onClick={() => void apply(t)} />)}
          {shared.length > 0 && <p className="px-3 pb-1 pt-2 text-xs font-semibold uppercase text-slate-500">Shared with me</p>}
          {shared.map(t => <TemplateRow key={t.id} t={t} onClick={() => void apply(t)} />)}
          {!list.length && <p className="px-3 py-2 text-sm text-slate-500">No saved layouts yet.</p>}
          <div className="my-1 border-t border-slate-200 dark:border-slate-700" />
          <MenuItem icon={<Save className="h-4 w-4" />} onClick={() => setSaving(true)}>Save current layout as…</MenuItem>
          <MenuItem icon={<Settings2 className="h-4 w-4" />} onClick={() => setManaging(true)}>Manage layouts</MenuItem>
        </div>
      </Menu>
      <SaveAs ws={ws} open={saving} onClose={() => setSaving(false)} count={mine.length} />
      <Manage ws={ws} open={managing} onClose={() => setManaging(false)} data={q.data} />
    </>
  );
}

function TemplateRow({ t, onClick }: { t: HomeTemplate; onClick: () => void }) {
  return (
    <MenuItem onClick={onClick} icon={t.active ? <Check className="h-4 w-4" /> : <span className="w-4" />}>
      <span className="flex-1 truncate">{t.name}</span>
      {!t.mine && <span className="text-xs text-slate-500">{t.owner_name}</span>}
      {t.mine && t.shared_with && <Share2 className="h-3.5 w-3.5 text-slate-500" aria-label="Shared" />}
    </MenuItem>
  );
}

function SaveAs({ ws, open, onClose, count }: { ws: string; open: boolean; onClose: () => void; count: number }) {
  const toast = useUi(s => s.toast);
  const [name, setName] = useState('');
  const submit = async () => {
    try {
      const t = await api.home.createTemplate(ws, { name });
      await queryClient.invalidateQueries({ queryKey: ['home', ws] });
      toast(`Saved “${t.name}”`, 'success'); setName(''); onClose();
    } catch (e) { toast(errorMessage(e), 'error'); }
  };
  return (
    <Modal open={open} onClose={onClose} title="Save current layout"
      footer={<DialogFooter onCancel={onClose} onSave={() => void submit()} disabled={!name.trim() || count >= MAX_TEMPLATES} />}>
      <Field label="Name" hint={`Saves the widgets on your home page now. You can keep up to ${MAX_TEMPLATES} layouts (${count} saved).`}>
        <Input autoFocus maxLength={60} value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Busy Saturday" onKeyDown={e => { if (e.key === 'Enter' && name.trim()) void submit(); }} />
      </Field>
    </Modal>
  );
}

type Data = Awaited<ReturnType<typeof api.home.templates>>;

function Manage({ ws, open, onClose, data }: { ws: string; open: boolean; onClose: () => void; data?: Data }) {
  const mine = data?.templates.filter(t => t.mine) ?? [];
  const canShare = Boolean(data && (data.can_share.teams.length || data.can_share.locations.length));
  return (
    <Modal open={open} onClose={onClose} title="Manage layouts" wide footer={<Button onClick={onClose}>Done</Button>}>
      {!mine.length && <p className="text-sm text-slate-500">You haven't saved any layouts yet.</p>}
      <ul className="divide-y divide-slate-200 dark:divide-slate-700">
        {mine.map(t => <ManageRow key={t.id} ws={ws} t={t} data={data!} canShare={canShare} />)}
      </ul>
      {canShare
        ? <p className="mt-3 text-xs text-slate-500">Sharing lets people in that team or building pick the layout. They get a copy; widgets they don't have access to are left out.</p>
        : <p className="mt-3 text-xs text-slate-500">Ask a manager if you'd like a layout shared with your team.</p>}
    </Modal>
  );
}

function ManageRow({ ws, t, data, canShare }: { ws: string; t: HomeTemplate; data: Data; canShare: boolean }) {
  const toast = useUi(s => s.toast);
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(t.name);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['home', ws, 'templates'] });
  const patch = async (b: Parameters<typeof api.home.updateTemplate>[2], ok: string) => {
    try { await api.home.updateTemplate(ws, t.id, b); await refresh(); toast(ok, 'success'); return true; } catch (e) { toast(errorMessage(e), 'error'); return false; }
  };
  const remove = async () => {
    if (!await ask(`Delete “${t.name}”? Your current home page stays as it is.`)) return;
    try { await api.home.deleteTemplate(ws, t.id); await refresh(); toast('Layout deleted', 'success'); } catch (e) { toast(errorMessage(e), 'error'); }
  };
  const shareValue = t.shared_with ? `${t.shared_with.type}:${t.shared_with.id}` : '';
  // Keep the current share visible even if I can no longer share there.
  const extra = t.shared_with && !(t.shared_with.type === 'team' ? data.can_share.teams : data.can_share.locations).some(x => x.id === t.shared_with!.id);
  return (
    <li className="flex flex-wrap items-center gap-3 py-3" data-testid="template-row">
      <div className="min-w-0 flex-1">
        {renaming ? (
          <div className="flex gap-2">
            <Input autoFocus maxLength={60} value={name} onChange={e => setName(e.target.value)} />
            <Button onClick={async () => { if (await patch({ name }, 'Renamed')) setRenaming(false); }} disabled={!name.trim()}>Save</Button>
            <Button variant="ghost" onClick={() => { setRenaming(false); setName(t.name); }}>Cancel</Button>
          </div>
        ) : (
          <>
            <p className="truncate font-medium">{t.name}{t.active && <span className="ml-2 rounded bg-brand-yellow px-1.5 py-0.5 text-xs font-semibold text-on-accent">In use</span>}</p>
            <p className="text-xs text-slate-500">{t.items.length} widget{t.items.length === 1 ? '' : 's'}{t.shared_with && <> · <Users className="inline h-3 w-3" /> Shared with {t.shared_with.name}</>}</p>
          </>
        )}
      </div>
      {canShare && (
        <Select aria-label={`Share ${t.name}`} className="w-56" value={shareValue} onChange={e => {
          const [type, id] = e.target.value.split(':');
          void patch({ shared_with: e.target.value ? { type: type as 'team' | 'location', id } : null }, e.target.value ? 'Layout shared' : 'Sharing turned off');
        }}>
          <option value="">Only me</option>
          {extra && <option value={shareValue}>{t.shared_with!.name}</option>}
          {data.can_share.locations.length > 0 && <optgroup label="Building">{data.can_share.locations.map(l => <option key={l.id} value={`location:${l.id}`}>{l.name}</option>)}</optgroup>}
          {data.can_share.teams.length > 0 && <optgroup label="Department or group">{data.can_share.teams.map(x => <option key={x.id} value={`team:${x.id}`}>{x.name}</option>)}</optgroup>}
        </Select>
      )}
      {!renaming && <Button variant="ghost" onClick={() => setRenaming(true)} aria-label={`Rename ${t.name}`}><Pencil className="h-4 w-4" />Rename</Button>}
      <Button variant="ghost" onClick={() => void remove()} aria-label={`Delete ${t.name}`}><Trash2 className="h-4 w-4" />Delete</Button>
    </li>
  );
}
